import { gsap, ScrollTrigger } from "../gsap.js";

function splitText(el) {
  const text = el.dataset.originalText || el.textContent;
  el.dataset.originalText = text;
  el.innerHTML = "";

  // 1. Split into words, wrap each word in a nowrap span containing char spans
  const charSpans = [];
  const words = text.split(/(\s+)/); // keep whitespace tokens

  words.forEach((segment) => {
    if (/^\s+$/.test(segment)) {
      el.appendChild(document.createTextNode(segment));
      return;
    }
    const wordWrap = document.createElement("span");
    wordWrap.style.whiteSpace = "nowrap";
    for (const char of segment) {
      const span = document.createElement("span");
      span.textContent = char;
      span.style.color = "rgba(255,255,255,0.3)";
      wordWrap.appendChild(span);
      charSpans.push(span);
    }
    el.appendChild(wordWrap);
  });

  // 2. Group word wrappers by line (based on offsetTop)
  const wordWrappers = el.querySelectorAll("span[style*='nowrap']");
  const lines = [];
  let currentLine = [];
  let currentTop = null;

  wordWrappers.forEach((ww) => {
    const top = ww.offsetTop;
    if (currentTop === null || top === currentTop) {
      currentLine.push(ww);
      currentTop = top;
    } else {
      lines.push(currentLine);
      currentLine = [ww];
      currentTop = top;
    }
  });
  if (currentLine.length) lines.push(currentLine);

  // 3. Wrap each line in overflow:hidden container with inner translateY wrapper
  el.innerHTML = "";
  const lineInners = [];

  lines.forEach((lineWords) => {
    const wrap = document.createElement("div");
    wrap.style.overflow = "hidden";

    const inner = document.createElement("div");
    lineWords.forEach((ww, j) => {
      inner.appendChild(ww);
      if (j < lineWords.length - 1) {
        inner.appendChild(document.createTextNode(" "));
      }
    });

    wrap.appendChild(inner);
    el.appendChild(wrap);
    lineInners.push(inner);
  });

  return { chars: charSpans, lineInners };
}

export default function initCalloutScroll() {
  const wrappers = document.querySelectorAll(".callout");

  if (!wrappers.length) return;

  wrappers.forEach((wrapper) => {
    const tiles = wrapper.querySelectorAll(".callout-tile");
    const total = tiles.length;

    if (total <= 1) {
      const navEl = wrapper.querySelector(".callout-nav");
      if (navEl) navEl.style.display = "none";
    }

    const progressBar = wrapper.querySelector(".callout-progress-bar");
    const currentEl = wrapper.querySelector(".callout-counter .current");
    const totalEl = wrapper.querySelector(".callout-counter .total");

    function pad(n) {
      return String(n).padStart(2, "0");
    }

    if (totalEl) totalEl.textContent = pad(total);
    if (currentEl) currentEl.textContent = pad(1);

    // Split h3 text into chars + lines, set initial state
    const tileData = [];
    tiles.forEach((tile, i) => {
      const h3 = tile.querySelector("h3");
      const { chars, lineInners } = h3
        ? splitText(h3)
        : { chars: [], lineInners: [] };

      tileData.push({ tile, h3, chars, lineInners });

      if (i === 0) {
        gsap.set(lineInners, { y: 0 });
      } else {
        gsap.set(tile, { autoAlpha: 0 });
        gsap.set(tile.querySelector(".image"), { autoAlpha: 0 });
        gsap.set(tile.querySelector(".eyebrow"), { autoAlpha: 0 });
        gsap.set(lineInners, { y: "110%" });
      }
    });

    // Re-split on resize to fix line breaks at different widths
    let resizeTimer;
    let lastWidth = wrapper.clientWidth;

    function resplitAll() {
      tileData.forEach((data, i) => {
        if (!data.h3) return;
        const { chars, lineInners } = splitText(data.h3);
        data.chars = chars;
        data.lineInners = lineInners;

        if (i === prevIndex) {
          // Current active tile — show lines, apply char fill
          gsap.set(lineInners, { y: 0 });
        } else {
          gsap.set(lineInners, { y: "110%" });
          chars.forEach((span) => {
            span.style.color = "rgba(255,255,255,0.3)";
          });
        }
      });
    }

    window.addEventListener("resize", () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const newWidth = wrapper.clientWidth;
        if (newWidth !== lastWidth) {
          lastWidth = newWidth;
          resplitAll();
        }
      }, 200);
    });

    const scrollSections = total * 2.5;
    const LG = 1280;
    const navbar = document.querySelector(".navbar-wrapper");
    const container = wrapper.closest(".container");

    let prevIndex = 0;

    ScrollTrigger.create({
      trigger: wrapper,
      invalidateOnRefresh: true,
      start: () => {
        const isLg = window.innerWidth >= LG;
        const currentNavbarH = navbar ? navbar.offsetHeight : 0;
        const currentSpacing = container
          ? parseFloat(getComputedStyle(container).paddingLeft)
          : 0;

        if (isLg) {
          wrapper.style.setProperty("--navbar-height", currentNavbarH + "px");
        } else {
          wrapper.style.removeProperty("--navbar-height");
        }

        return "top top+=" + (isLg ? currentNavbarH : currentSpacing);
      },
      end: () => "+=" + window.innerHeight * scrollSections,
      pin: true,
      anticipatePin: 1,
      scrub: 0.3,
      onRefresh: () => {
        const newWidth = wrapper.clientWidth;
        if (newWidth !== lastWidth) {
          lastWidth = newWidth;
          resplitAll();
        }
      },
      onUpdate: (self) => {
        const progress = self.progress;
        const scaled = progress * total;
        const currentIndex = Math.min(Math.floor(scaled), total - 1);
        const itemProgress = scaled - currentIndex;

        // Detect slide change
        if (currentIndex !== prevIndex) {
          const { tile, lineInners } = tileData[currentIndex];
          const img = tile.querySelector(".image");
          const eyebrow = tile.querySelector(".eyebrow");

          gsap.set(tile, { autoAlpha: 1 });
          gsap.to(img, { autoAlpha: 1, duration: 1, overwrite: true });
          gsap.to(eyebrow, { autoAlpha: 1, duration: 1, overwrite: true });

          // Lines slide up from slit with stagger
          gsap.to(lineInners, {
            y: 0,
            duration: 0.8,
            stagger: 0.12,
            ease: "power3.out",
            overwrite: true,
          });

          // Hide previous tile + reset
          const prev = tileData[prevIndex];
          gsap.set(prev.tile, { autoAlpha: 0 });
          gsap.set(prev.lineInners, { y: "110%" });
          prev.chars.forEach((span) => {
            span.style.color = "rgba(255,255,255,0.3)";
          });

          prevIndex = currentIndex;
        }

        // Fill chars one by one - complete at 70% of item progress
        const { chars } = tileData[currentIndex];
        const fillCount = Math.round(
          Math.min(itemProgress / 0.8, 1) * chars.length
        );
        chars.forEach((span, idx) => {
          span.style.color =
            idx < fillCount ? "white" : "rgba(255,255,255,0.3)";
        });

        // Progress bar
        if (progressBar) {
          const barProgress = Math.min(itemProgress, 1) * 100;
          progressBar.style.width = `${barProgress}%`;
        }

        // Counter
        if (currentEl) {
          currentEl.textContent = pad(currentIndex + 1);
        }
      },
    });
  });
}
